import { match, P } from 'ts-pattern';
import {
  SETTLEMENT_PERIOD_TYPE,
  SETTLEMENT_PRODUCT_MANAGEMENT_TYPE,
  SETTLEMENT_SEARCH_PERIOD_TYPE,
} from '../../constants';
import { SettlementByBrandForm, SettlementForm } from '../../types';
import { SettlementItemsByBrandQueryParams, SettlementItemsQueryParams } from '../../types/apis';
import { ColumnFiltersState, ColumnSort, PaginationState } from '@tanstack/react-table';
import moment from '../../../../lib/moment';
import {
  SettlementItemsBulkUpdateResponseSettlementItemDtoStatusEnum,
  SettlementItemsFindAllResponseSettlementItemsProductManagementTypeDtoTypeEnum,
} from '../../../../types/api/settlement-items';
import { ValueOf } from '../../../../types/common/utils';
import { SettlementStatus } from '../../types/client';

const convertSearchPeriodType = (
  searchPeriodType: SettlementForm['searchPeriodType'],
): SettlementItemsQueryParams['searchPeriodType'] => {
  return match<SettlementForm['searchPeriodType'], SettlementItemsQueryParams['searchPeriodType']>(searchPeriodType)
    .with(SETTLEMENT_SEARCH_PERIOD_TYPE.정산_기준일, () => 'baseDate')
    .with(SETTLEMENT_SEARCH_PERIOD_TYPE.정산_예정일, () => 'dueDate')
    .with(SETTLEMENT_SEARCH_PERIOD_TYPE.정산_완료일, () => 'completeDate')
    .otherwise(() => {
      throw new Error(`존재할 수 없는 searchPeriodType: ${searchPeriodType}`);
    });
};

const convertPeriodType = (periodType: SettlementForm['periodType']): SettlementItemsQueryParams['periodType'] => {
  return match<SettlementForm['periodType'], SettlementItemsQueryParams['periodType']>(periodType)
    .with(SETTLEMENT_PERIOD_TYPE.일간_정산, () => 'daily')
    .with(SETTLEMENT_PERIOD_TYPE.월간_정산, () => 'monthly')
    .with(SETTLEMENT_PERIOD_TYPE.전체, () => undefined)
    .otherwise(() => {
      throw new Error(`존재할 수 없는 periodType: ${periodType}`);
    });
};

const convertSort = (sort: ColumnSort | undefined): SettlementItemsQueryParams['sort'] => {
  return match(sort)
    .with({ id: 'id', desc: P.boolean }, ({ desc }) => (desc ? '-id' : 'id'))
    .with({ id: 'dueDate', desc: P.boolean }, ({ desc }) => (desc ? '-dueDate' : 'dueDate'))
    .with({ id: 'baseDate', desc: P.boolean }, ({ desc }) => (desc ? '-baseDate' : 'baseDate'))
    .with({ id: 'completeDate', desc: P.boolean }, ({ desc }) => (desc ? '-completeDate' : 'completeDate'))
    .with(undefined, () => undefined)
    .otherwise(() => {
      throw new Error(`지원하지 않는 정렬 필드: ${sort?.id}`);
    });
};

const convertIds = (ids: unknown): number[] | undefined => {
  return match(ids)
    .with(P.string, (guardedIds) => guardedIds.split(',').map((id) => Number(id)))
    .with(undefined, () => undefined)
    .otherwise(() => {
      throw new Error(`유효하지 않은 필터: ${ids}`);
    });
};

const convertBrandId = (brandId: unknown): number | undefined => {
  return match(brandId)
    .with(P.string, (guardedBrandId) => Number(guardedBrandId))
    .with(undefined, () => undefined)
    .otherwise(() => {
      throw new Error(`유효하지 않은 필터: ${brandId}`);
    });
};

const convertBrandName = (brandName: unknown): string | undefined => {
  return match(brandName)
    .with(P.string, (guardedBrandName) => guardedBrandName)
    .with(undefined, () => undefined)
    .otherwise(() => {
      throw new Error(`유효하지 않은 필터: ${brandName}`);
    });
};

const convertOrderId = (orderId: unknown): string | undefined => {
  return match(orderId)
    .with(P.string, (guardedOrderId) => guardedOrderId)
    .with(undefined, () => undefined)
    .otherwise(() => {
      throw new Error(`유효하지 않은 필터: ${orderId}`);
    });
};

const convertAccountHolder = (accountHolder: unknown): string | undefined => {
  return match(accountHolder)
    .with(P.string, (guardedAccountHolder) => guardedAccountHolder)
    .with(undefined, () => undefined)
    .otherwise(() => {
      throw new Error(`유효하지 않은 필터: ${accountHolder}`);
    });
};

const convertAccountNumber = (accountNumber: unknown): string | undefined => {
  return match(accountNumber)
    .with(P.string, (guardedAccountNumber) => guardedAccountNumber)
    .with(undefined, () => undefined)
    .otherwise(() => {
      throw new Error(`유효하지 않은 필터: ${accountNumber}`);
    });
};

const convertBankName = (bankName: unknown): string | undefined => {
  return match(bankName)
    .with(P.string, (guardedBankName) => guardedBankName)
    .with(undefined, () => undefined)
    .otherwise(() => {
      throw new Error(`유효하지 않은 필터: ${bankName}`);
    });
};

export const convertSettlementFormToParams = ({
  form,
  sort,
  filter,
  pagination,
  status,
}: {
  form: SettlementForm;
  sort: ColumnSort;
  filter: ColumnFiltersState;
  pagination: PaginationState;
  status: 'ready' | 'completed' | 'hold';
}): SettlementItemsQueryParams => {
  const ids = filter.find(({ id }) => id === 'id')?.value;
  const brandId = filter.find(({ id }) => id === 'brandId')?.value;
  const brandName = filter.find(({ id }) => id === 'brandName')?.value;
  const orderId = filter.find(({ id }) => id === 'orderId')?.value;
  const accountHolder = filter.find(({ id }) => id === 'accountHolder')?.value;
  const accountNumber = filter.find(({ id }) => id === 'accountNumber')?.value;
  const bankName = filter.find(({ id }) => id === 'bankName')?.value;

  return {
    status,
    searchPeriodType: convertSearchPeriodType(form.searchPeriodType),
    searchPeriodStartDate: form.searchPeriodStartDate
      ? moment(form.searchPeriodStartDate).startOf('day').toISOString()
      : undefined,
    searchPeriodEndDate: form.searchPeriodEndDate
      ? moment(form.searchPeriodEndDate).endOf('day').toISOString()
      : undefined,
    periodType: convertPeriodType(form.periodType),
    monthlyExecutionPeriod: form.monthlyExecutionPeriod ? Number(form.monthlyExecutionPeriod) : undefined,
    monthlyExecutionDay: form.monthlyExecutionDay ? Number(form.monthlyExecutionDay) : undefined,
    sort: convertSort(sort),
    ids: convertIds(ids),
    brandId: convertBrandId(brandId),
    brandName: convertBrandName(brandName),
    orderId: convertOrderId(orderId),
    accountHolder: convertAccountHolder(accountHolder),
    accountNumber: convertAccountNumber(accountNumber),
    bankName: convertBankName(bankName),
    page: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
  };
};

const convertBrandIds = (brandIds: unknown): number[] | undefined => {
  return match(brandIds)
    .with('', () => undefined)
    .with(P.string, (guardedIds) => guardedIds.split(',').map((id) => Number(id)))
    .otherwise(() => {
      throw new Error(`지원하지 않는 brandIds: ${brandIds}`);
    });
};

const convertBrandNames = (brandNames: unknown): string[] | undefined => {
  return match(brandNames)
    .with('', () => undefined)
    .with(P.string, (guardedIds) => guardedIds.split(','))
    .otherwise(() => {
      throw new Error(`지원하지 않는 brandNames: ${brandNames}`);
    });
};

const covertProductManagementType = (
  productManagementType: ValueOf<typeof SETTLEMENT_PRODUCT_MANAGEMENT_TYPE>,
): SettlementItemsFindAllResponseSettlementItemsProductManagementTypeDtoTypeEnum => {
  return match<
    ValueOf<typeof SETTLEMENT_PRODUCT_MANAGEMENT_TYPE>,
    SettlementItemsFindAllResponseSettlementItemsProductManagementTypeDtoTypeEnum
  >(productManagementType)
    .with(SETTLEMENT_PRODUCT_MANAGEMENT_TYPE.위탁생산, () => 'consignmentManufacture')
    .with(SETTLEMENT_PRODUCT_MANAGEMENT_TYPE.입고상품, () => 'income')
    .with(SETTLEMENT_PRODUCT_MANAGEMENT_TYPE.입점상품, () => 'enter')
    .otherwise(() => {
      throw new Error(`존재할 수 없는 productManagementType: ${productManagementType}`);
    });
};

export const convertSettlementByBrandFormToParams = ({
  form,
  pagination,
}: {
  form: SettlementByBrandForm;
  pagination: PaginationState;
}): SettlementItemsByBrandQueryParams => {
  if (!form.productManagementTypes) {
    return {
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      brandIds: form.searchType === 'ids' ? convertBrandIds(form.searchText) : undefined,
      brandNames: form.searchType === 'names' ? convertBrandNames(form.searchText) : undefined,
      periodType: convertPeriodType(form.periodType),
    };
  }

  const productManagementTypes = Array.isArray(form.productManagementTypes)
    ? form.productManagementTypes.map((type) => covertProductManagementType(type))
    : covertProductManagementType(form.productManagementTypes);

  return {
    page: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    brandIds: form.searchType === 'ids' ? convertBrandIds(form.searchText) : undefined,
    brandNames: form.searchType === 'names' ? convertBrandNames(form.searchText) : undefined,
    periodType: convertPeriodType(form.periodType),
    productManagementTypes,
  };
};

export const convertStatus = (
  status: SettlementStatus,
): SettlementItemsBulkUpdateResponseSettlementItemDtoStatusEnum => {
  return match<SettlementStatus, SettlementItemsBulkUpdateResponseSettlementItemDtoStatusEnum>(status)
    .with('대기', () => 'ready')
    .with('보류', () => 'hold')
    .with('완료', () => 'completed')
    .otherwise(() => {
      throw new Error(`지원하지 않는 상태 필드: ${status}`);
    });
};
